import defaultContent from "../../../content/downlineProfile";
import type { SopStatusKpis, SopSubscription } from "../Tabs/SopStatus/sopStatusData";
import { internationalSponsor, PartyInfo } from "../../../services/Profile/profileAPI.types";
import {
  transformAdditionalRegInfo,
  transformAwardInfo,
  transformKeyDates,
} from "../KeyInformation/Transforms/transformKeyInformation";
import { SerializedEditorState, SerializedLexicalNode } from "lexical";
import { TFunction } from "i18next";

export interface DecimalValue {
  value: number;
  formatted: string;
}

export interface SeriesPoint {
  period: number;
  month: number;
  value: DecimalValue;
}

export interface PerformanceYearSeries {
  performanceYear: number;
  points: SeriesPoint[];
}

export interface KpiSeries {
  kpi: string;
  yoy?: string;
  yoyDifference?: string;
  years: PerformanceYearSeries[] | null;
  range?: number[] | null;
}

export const PY_MONTHS = [9, 10, 11, 12, 1, 2, 3, 4, 5, 6, 7, 8];

export type NormalizedSeriesData = {
  month: number;
  monthLabel: string;
  values: { kpi: string; point: SeriesPoint | null }[];
};

export type SponsorType =
  | "sponsor"
  | "sponsorPlatinum"
  | "platinum"
  | "fosterSponsorPlatinum"
  | "internationalSponsor"
  | "fosterSponsor";

export interface LegendItemConfig {
  dataKey: string;
  color: string;
  dash?: string;
  label: string;
}

export type AdditionalInformationLabelKey = keyof typeof defaultContent.keyInformation.additionalInformationLabels;
export type KeyDatesLabelKey = keyof typeof defaultContent.keyInformation.keyDatesLabels;
export type PerformanceMetricsLabel = keyof typeof defaultContent.performanceMetrics;

export interface InternationalSponsorWithFlagImage extends internationalSponsor {
  flagImage?: string;
}
type aboInfo = NonNullable<Parameters<typeof transformKeyDates>[0]>;
export interface AboInfoWithInternationalSponsorFlagImage extends aboInfo {
  internationalSponsor: InternationalSponsorWithFlagImage;
}
export interface KpiSeriesWithImage extends KpiSeries {
  chartImage?: string | null;
}

export interface DownlineProfileDocumentProps {
  profileBanner: {
    displayName: string;
    aboNumber: string;
    award: {
      highestAwardDate?: number;
      highestAward?: number;
      isPlatinumOrAbove?: boolean;
      isEmeraldAndAbove?: boolean;
      isDiamondAndAbove?: boolean;
    };
    lineageNumber?: number;
    isRootAbo?: boolean;
    tags?: {
      isOnSop?: boolean;
      isOnAutoRenewal?: boolean;
      hasNotes?: boolean;
    };
    phone?: string;
    phoneHref?: string;
    email?: string;
    whatsAppContactNumber?: string;
    hideWhatsApp?: boolean;
    isInWatchlist?: boolean;
    imageRefUrl?: string;
    privacyFlag?: boolean;
  };
  keyInfo: {
    aboInfo?: AboInfoWithInternationalSponsorFlagImage;
    orderInfo?: Parameters<typeof transformKeyDates>[1];
    pinAwardSummary?: Parameters<typeof transformAwardInfo>[0];
    additionalRegInfo?: Parameters<typeof transformAdditionalRegInfo>[0];
    partyInfo?: PartyInfo[];
    isPlatinumOrAbove?: boolean;
    isEmeraldAndAbove?: boolean;
    isDiamondAndAbove?: boolean;
    profileConfig?: {
      showAutoRenewal?: boolean;
      showRenewalDate?: boolean;
      showContractSigned?: boolean;
      showOrderBlock?: boolean;
      showSponsorBlock?: boolean;
      showRenewalConsent?: boolean;
    };
    imageData: PdfFetchProfileImages | undefined;
  };
  sopStatus?: {
    kpis: SopStatusKpis;
    subscriptions: SopSubscription[];
  };
  performanceMetrics: {
    series?: KpiSeriesWithImage[] | null;
    selectedKpis?: string[];
    locale?: string;
    endPeriod?: number;
  };
  notes: {
    lastEdited: number | null;
    content: SerializedEditorState<SerializedLexicalNode> | null;
    notesImage?: string | null;
  };
  privacyConfig: {
    privacyFlag?: boolean;
    nameFormat?: string;
    portalLocale?: string;
  };
  formatConfig: {
    addressFormat?: string;
    addressLineDivider?: any;
    dateFormat?: "long" | "short";
  };
  tFunction: TFunction<readonly ["downlineProfile", "common", "awardLabels"]>;
}

export interface PdfFetchProfileImages {
  profileImages: {
    aff: string;
    abo: string;
    profileImage: string;
  }[];
  partyImages: {
    aff: string;
    abo: string;
    partyId: string;
    profileImage: string;
  }[];
}
